import { useEffect } from "react";
import { RestomenuError } from "@restomenu/core/client";
import { api } from "@/core/api-client";
import { useAppDispatch } from "@/core/hooks";
import { setBrandId } from "@/core/app/reducer";

/**
 * Точку не удалось разрешить, и показать гостю нечего.
 *
 * Два статуса, для пользователя неразличимые:
 *
 *   404 — точки нет либо она выключена. Сервер отвечает одинаково на оба
 *         случая сознательно; код в теле — 1509 `domain.point.not_found`;
 *   400 — `pointId` из URL не проходит валидацию (`transport.invalid`,
 *         path `id`). Проверено живьём: адрес вида
 *         `11111111-2222-3333-4444-555555555555` отбивается именно так.
 *         Гость правит не ссылку, а ничего — для него это то же
 *         «заведение недоступно», и проваливать его к пустому меню со
 *         снекбаром об ошибке нельзя.
 *
 * Различаем по статусу, а не по коду: у GET /guest/points/{id} схема кода
 * не обещает вовсе, хотя контур его и присылает.
 */
export function isPointUnavailable(error: unknown): boolean {
  return (
    error instanceof RestomenuError &&
    (error.status === 404 || error.status === 400)
  );
}

/**
 * Гостевая точка.
 *
 * Единственный источник brandId: во входе гостевых маршрутов его нет
 * вовсе — все семь адресуются pointId, а бренд выводится из точки на
 * сервере. Меню brandId не нужен; нужен замороженным до Ф7 заказам
 * (спека §4.4).
 */
export function useGuestPoint(pointId: string | null) {
  const dispatch = useAppDispatch();
  const query = api.useQuery(
    "get",
    "/guest/points/{id}",
    { params: { path: { id: pointId ?? "" } } },
    { enabled: Boolean(pointId) },
  );

  const brandId = query.data?.brandId;
  const resolved = query.isSuccess;
  const failed = query.isError;

  useEffect(() => {
    if (resolved && brandId) {
      dispatch(setBrandId(brandId));
      return;
    }
    // Точка не разрешилась — гасим бренд предыдущей. Иначе замороженные
    // до Ф7 заказы продолжили бы работать под чужим брендом: сага
    // очистки корзины смотрит на экшен, а без него экшена нет.
    if (failed) dispatch(setBrandId(null));
  }, [resolved, failed, brandId, dispatch]);

  return {
    brandId,
    // isLoading, а не isPending: у отключённого запроса isPending в
    // TanStack v5 не сбрасывается никогда, и при пустом pointId экран
    // залип бы в вечной загрузке.
    isLoading: query.isLoading,
    isUnavailable: isPointUnavailable(query.error),
    // Настоящий сбой отделён от «точки нет»: у них разные экраны.
    isError: query.isError && !isPointUnavailable(query.error),
  };
}
